import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { IApp } from "@/types";
import { authEndpoint, sendRequest, setBearerToken } from "@/utils";
import { URLs } from "@/constants";
import { useRouter } from "vue-router";
import Auth from "@/utils/auth";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<IApp.IUser>();
  const status = ref<IApp.ILoadingStatus>(IApp.ILoadingStatus.Idle);
  const registerStatus = ref<IApp.ILoadingStatus>(IApp.ILoadingStatus.Idle);
  const confirmPasswordResetStatus = ref<IApp.ILoadingStatus>(IApp.ILoadingStatus.Idle);
  const router = useRouter();
  const isAuthenticated = computed(() => status.value == IApp.ILoadingStatus.Success && user.value);
  const isLoading = computed(() => status.value == IApp.ILoadingStatus.Loading);
  const productAccessErrorMessage = "You don't have permission to access to this product";

  function hasPermission(permission: Auth.Permission) {
    const permissions = user.value?.permissions.find(
      (p) => p.productId === Auth.THOR_AI_PRODUCT_ID
    );
    return permissions?.permissions.includes(permission) ?? false;
  }

  function hasProductAccess(value: IApp.IUser) {
    return value.permissions.some((p) => p.productId === Auth.THOR_AI_PRODUCT_ID);
  }

  function setUser(value: IApp.IUser | undefined) {
    user.value = value;
  }

  function setStatus(value: IApp.ILoadingStatus) {
    status.value = value;
  }

  async function login(detail: { email: string; password: string }) {
    setStatus(IApp.ILoadingStatus.Loading);
    const response = await sendRequest<{ accessToken: string }>({
      method: "POST",
      url: authEndpoint(URLs.API_AUTH_LOGIN),
      data: detail
    });
    const { status: responseStatus, data } = response;
    if (responseStatus) {
      setBearerToken(data.accessToken);
      const userResponse = await getUserInfo();
      setStatus(userResponse.status ? IApp.ILoadingStatus.Success : IApp.ILoadingStatus.Error);
      if (!userResponse.status) {
        return userResponse;
      }
    } else {
      setStatus(IApp.ILoadingStatus.Error);
    }
    return response;
  }

  async function register(data: { email: string; password: string; name: string }) {
    const response = await sendRequest({
      url: authEndpoint(URLs.API_AUTH_REGISTER),
      method: "post",
      data: {
        email: data.email,
        password: data.password,
        name: data.name
      }
    });

    if (response.status) {
      registerStatus.value = IApp.ILoadingStatus.Success;
    } else {
      registerStatus.value = IApp.ILoadingStatus.Error;
    }

    return response;
  }

  async function confirmPasswordReset(data: { email: string; password: string; code: string }) {
    const response = await sendRequest({
      url: authEndpoint(URLs.API_AUTH_CONFIRM_PASSWORD_RESET),
      method: "post",
      data: {
        email: data.email,
        newPassword: data.password,
        code: data.code
      }
    });

    if (response.status) {
      confirmPasswordResetStatus.value = IApp.ILoadingStatus.Success;
    } else {
      confirmPasswordResetStatus.value = IApp.ILoadingStatus.Error;
    }

    return response;
  }

  async function passwordReset(data: { email: string }) {
    return await sendRequest({
      url: authEndpoint(URLs.API_AUTH_PASSWORD_RESET),
      method: "post",
      data: {
        email: data.email
      }
    });
  }

  async function getUserInfo() {
    setStatus(IApp.ILoadingStatus.Loading);
    const response = await sendRequest<IApp.IUser>({
      url: authEndpoint(URLs.API_AUTH_INFO),
      method: "GET"
    });
    if (response.status) {
      if (!hasProductAccess(response.data)) {
        setUser(undefined);
        setBearerToken("");
        setStatus(IApp.ILoadingStatus.Error);
        return {
          status: false,
          message: productAccessErrorMessage,
          title: "Authentication Error",
          type: "ProductAccessDenied"
        };
      }
      user.value = response.data;
      setStatus(IApp.ILoadingStatus.Success);
    } else {
      setStatus(IApp.ILoadingStatus.Error);
    }
    return response;
  }

  function logout() {
    setUser(undefined);
    setBearerToken("");
    router.push({
      name: "login"
    });
  }

  async function confirmEmail(data: { email: string; code: string }) {
    return await sendRequest({
      url: authEndpoint(URLs.API_AUTH_EMAIL_CONFIRM),
      method: "post",
      data: {
        email: data.email,
        code: data.code
      }
    });
  }

  async function resendConfirmEmail(data: { email: string }) {
    return await sendRequest({
      url: authEndpoint(URLs.API_AUTH_EMAIL_CONFIRM_RESEND_CODE),
      method: "post",
      data: {
        email: data.email
      }
    });
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    status,
    registerStatus,
    setUser,
    login,
    register,
    logout,
    getUserInfo,
    confirmEmail,
    resendConfirmEmail,
    hasPermission,
    passwordReset,
    confirmPasswordReset
  };
});
